import React, { useState, useEffect } from 'react';
import { Mail, Download, Home, MessageSquare, Phone, Calendar, Copy, Check, Send, ExternalLink, CheckSquare, Square, Clock, StickyNote, Trash2, MailCheck } from 'lucide-react';
import s from './adminStyles';
import { authHeaders } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Partner routing target. All qualified leads flow through Pascal at Nelemans
 * before reaching the end agents, so the "Doorsturen"-CTA on each row is
 * hard-wired here. Kept as a const rather than an env var because it's
 * business logic (who receives the lead), not deployment configuration.
 * Change these two values to re-route the partner pipeline.
 */
const PARTNER = {
  name: 'Pascal (Nelemans)',
  // E.164 without the "+" — this is the format wa.me expects.
  whatsappNumber: '34627608361',
  email: 'pascal@nelemans.es',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('nl-NL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const ctaIcon = (cta) => {
  switch (cta) {
    case 'viewing': return <Calendar size={14} />;
    case 'callback': return <Phone size={14} />;
    case 'info': return <Mail size={14} />;
    case 'newsletter': return <MessageSquare size={14} />;
    default: return <Mail size={14} />;
  }
};

const ctaLabel = (cta) => {
  switch (cta) {
    case 'viewing': return 'Bezichtiging';
    case 'callback': return 'Terugbelverzoek';
    case 'info': return 'Info-aanvraag';
    case 'newsletter': return 'Newsletter';
    default: return cta || 'Aanvraag';
  }
};

// Build a human-readable Dutch text block ready to paste in WhatsApp/email
const buildLeadText = (lead) => {
  const lines = [
    `Nieuwe lead via CasaOthers — ${ctaLabel(lead.cta_type)}`,
    '',
    `Naam: ${lead.first_name || ''} ${lead.last_name || ''}`.trim(),
    `Email: ${lead.email || '-'}`,
  ];
  if (lead.phone) lines.push(`Telefoon: ${lead.phone}`);
  lines.push(`Datum: ${formatDate(lead.created_at)}`);
  if (lead.property_title) {
    lines.push('');
    lines.push(`Woning: ${lead.property_title}`);
    if (lead.property_ref) lines.push(`Referentie: ${lead.property_ref}`);
    if (lead.property_location) lines.push(`Locatie: ${lead.property_location}`);
    if (lead.property_region) lines.push(`Regio: ${lead.property_region}`);
    if (lead.property_price) lines.push(`Prijs: €${Number(lead.property_price).toLocaleString('nl-NL')}`);
    if (lead.property_url) lines.push(`Link: ${lead.property_url}`);
  }
  if (lead.message) {
    lines.push('');
    lines.push('Bericht klant:');
    lines.push(lead.message);
  }
  return lines.join('\n');
};

// Default status palette — overridden once API returns its own labels.
const DEFAULT_STATUS_OPTIONS = {
  new: 'Nieuw',
  contacted: 'Contact opgenomen',
  forwarded: 'Doorgestuurd naar partner',
  viewing_scheduled: 'Bezichtiging gepland',
  negotiating: 'In onderhandeling',
  sold: 'Verkocht',
  closed: 'Gesloten',
};

const STATUS_COLORS = {
  new: { bg: '#FEF3C7', fg: '#92400E', dot: '#F59E0B' },
  contacted: { bg: '#DBEAFE', fg: '#1E40AF', dot: '#3B82F6' },
  forwarded: { bg: '#E0E7FF', fg: '#3730A3', dot: '#6366F1' },
  viewing_scheduled: { bg: '#FAE8FF', fg: '#86198F', dot: '#A855F7' },
  negotiating: { bg: '#FED7AA', fg: '#9A3412', dot: '#F97316' },
  sold: { bg: '#DCFCE7', fg: '#166534', dot: '#22C55E' },
  closed: { bg: '#E5E7EB', fg: '#374151', dot: '#6B7280' },
};

const LeadsPanel = () => {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [toast, setToast] = useState('');
  const [selected, setSelected] = useState(new Set()); // set of lead indices
  const [statusOptions, setStatusOptions] = useState(DEFAULT_STATUS_OPTIONS);
  const [statusFilter, setStatusFilter] = useState('all');
  const [savingStatusId, setSavingStatusId] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/leads`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        setLeads(d.items || []);
        setTotal(d.total || 0);
        if (d.status_options) setStatusOptions(d.status_options);
      })
      .catch(console.error);
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2200);
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    setSavingStatusId(leadId);
    try {
      const res = await fetch(`${API_URL}/api/leads/${leadId}/status`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('status update failed');
      const data = await res.json();
      setLeads(prev => prev.map(l =>
        l.id === leadId
          ? {
              ...l,
              status: newStatus,
              status_updated_at: new Date().toISOString(),
              status_history: [...(l.status_history || []), data.history_entry],
            }
          : l
      ));
      showToast('Status bijgewerkt');
    } catch {
      showToast('Status update mislukt');
    } finally {
      setSavingStatusId(null);
    }
  };

  const saveLeadNote = async (leadId, note) => {
    try {
      const res = await fetch(`${API_URL}/api/leads/${leadId}/note`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      });
      if (!res.ok) throw new Error('note save failed');
      const data = await res.json();
      setLeads(prev => prev.map(l =>
        l.id === leadId ? { ...l, note, note_updated_at: data.note_updated_at } : l
      ));
      showToast('Notitie opgeslagen');
    } catch {
      showToast('Notitie opslaan mislukt');
    }
  };

  // Hard-delete one lead with confirmation. Removes from local state on success.
  const deleteLead = async (lead) => {
    const fullName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || lead.email;
    if (!window.confirm(`Weet je zeker dat je de lead van "${fullName}" definitief wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`)) {
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/leads/${lead.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('delete failed');
      setLeads(prev => prev.filter(l => l.id !== lead.id));
      setTotal(t => Math.max(0, t - 1));
      setSelected(prev => {
        const next = new Set();
        // Indices shift after removal — rebuild selection set against new array
        prev.forEach(() => {});
        return next;
      });
      showToast('Lead verwijderd');
    } catch {
      showToast('Verwijderen mislukt');
    }
  };

  // Send the welcome newsletter to a single lead on demand. Backend
  // dedupes so accidental double-clicks don't spam the recipient; if the
  // admin actually wants a resend the endpoint accepts `force: true`
  // (not exposed here yet — we lean on the dedupe as the sane default).
  const sendWelcomeMail = async (lead) => {
    if (!lead.email) return;
    if (!window.confirm(`Welkomstmail versturen naar ${lead.email}?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/newsletters/welcome/send-to`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: lead.email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'send failed');
      if (data.skipped) {
        showToast('Welkomstmail was al eerder verstuurd naar dit adres.');
      } else {
        showToast('Welkomstmail verstuurd');
      }
    } catch (e) {
      showToast('Verzenden mislukt');
    }
  };

  // Bulk-delete all currently selected leads with confirmation.
  const bulkDelete = async () => {
    const chosen = leads.filter((_, i) => selected.has(i));
    if (chosen.length === 0) return;
    if (!window.confirm(`Weet je zeker dat je ${chosen.length} lead${chosen.length === 1 ? '' : 's'} definitief wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`)) {
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/leads/bulk-delete`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: chosen.map(l => l.id) }),
      });
      if (!res.ok) throw new Error('bulk delete failed');
      const data = await res.json();
      const deletedIds = new Set(chosen.map(l => l.id));
      setLeads(prev => prev.filter(l => !deletedIds.has(l.id)));
      setTotal(t => Math.max(0, t - (data.deleted || chosen.length)));
      setSelected(new Set());
      showToast(`${data.deleted || chosen.length} lead${(data.deleted || chosen.length) === 1 ? '' : 's'} verwijderd`);
    } catch {
      showToast('Bulk verwijderen mislukt');
    }
  };

  const copyToClipboard = async (text, idx, label = 'Lead gekopieerd') => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedIdx(idx);
      showToast(label);
      setTimeout(() => setCopiedIdx(null), 1800);
    } catch (e) {
      showToast('Kopiëren mislukt');
    }
  };

  const openWhatsApp = (lead) => {
    const text = encodeURIComponent(buildLeadText(lead));
    // No fixed recipient: wa.me without a number opens WhatsApp's chat picker
    // so the admin can drop the lead straight into the WhatsApp GROUP (or any
    // chat) of their choice. Fire-and-forget status bump to `forwarded`.
    window.open(
      `https://wa.me/?text=${text}`,
      '_blank',
      'noopener,noreferrer'
    );
    if (lead.status !== 'forwarded' && lead.status !== 'sold' && lead.status !== 'closed') {
      updateLeadStatus(lead.id, 'forwarded').catch(() => { /* status bump is best-effort */ });
    }
  };

  const openEmail = (lead) => {
    const subject = encodeURIComponent(
      `Lead CasaOthers — ${lead.first_name || ''} ${lead.last_name || ''}`.trim() +
      (lead.property_title ? ` (${lead.property_title})` : '')
    );
    const body = encodeURIComponent(buildLeadText(lead));
    // Pre-address the mail to the partner so the admin's mail client opens
    // ready-to-send. `mailto:` respects the user's default handler
    // (Outlook/Gmail/Apple Mail) so no extra service is needed.
    window.location.href = `mailto:${PARTNER.email}?subject=${subject}&body=${body}`;
    if (lead.status !== 'forwarded' && lead.status !== 'sold' && lead.status !== 'closed') {
      updateLeadStatus(lead.id, 'forwarded').catch(() => { /* status bump is best-effort */ });
    }
  };

  const exportCSV = () => {
    const header = 'Datum,Voornaam,Achternaam,Email,Telefoon,Bron,Type,Woning,Referentie,Plaats,Prijs,Bericht\n';
    const rows = leads
      .map(l => [
        l.created_at, l.first_name, l.last_name || '', l.email, l.phone || '',
        l.source || '', l.cta_type || '',
        (l.property_title || '').replace(/,/g, ';'),
        l.property_ref || '',
        l.property_location || '',
        l.property_price || '',
        (l.message || '').replace(/[,\n]/g, ' '),
      ].map(x => `"${String(x).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `casaothers-leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // --- Bulk selection helpers ---
  const toggleOne = (idx) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(prev => prev.size === leads.length ? new Set() : new Set(leads.map((_, i) => i)));
  };
  const clearSelection = () => setSelected(new Set());

  const selectedLeads = () => leads.filter((_, i) => selected.has(i));

  const bulkCopy = () => {
    const chosen = selectedLeads();
    if (chosen.length === 0) return;
    const separator = '\n\n────────────────────\n\n';
    const text = chosen.map(buildLeadText).join(separator);
    copyToClipboard(text, -1, `${chosen.length} leads gekopieerd`);
  };

  const bulkWhatsApp = () => {
    const chosen = selectedLeads();
    if (chosen.length === 0) return;
    const separator = '\n\n────────────────────\n\n';
    const text = encodeURIComponent(chosen.map(buildLeadText).join(separator));
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const bulkExportCSV = () => {
    const chosen = selectedLeads();
    if (chosen.length === 0) return;
    const header = 'Datum,Voornaam,Achternaam,Email,Telefoon,Bron,Type,Woning,Referentie,Plaats,Prijs,Bericht\n';
    const rows = chosen
      .map(l => [
        l.created_at, l.first_name, l.last_name || '', l.email, l.phone || '',
        l.source || '', l.cta_type || '',
        (l.property_title || '').replace(/,/g, ';'),
        l.property_ref || '',
        l.property_location || '',
        l.property_price || '',
        (l.message || '').replace(/[,\n]/g, ' '),
      ].map(x => `"${String(x).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `casaothers-leads-selectie-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const allSelected = leads.length > 0 && selected.size === leads.length;

  // Filter the list by status (purely client-side, doesn't refetch)
  const visibleLeads = statusFilter === 'all'
    ? leads
    : leads.filter(l => (l.status || 'new') === statusFilter);

  // Status counts for the filter dropdown
  const statusCounts = leads.reduce((acc, l) => {
    const k = l.status || 'new';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <div>
      <div style={s.header}>
        <h2 style={s.sectionTitle}>Leads ({total})</h2>
        {leads.length > 0 && (
          <button style={s.btnPrimary} onClick={exportCSV} data-testid="export-leads-csv-button">
            <Download size={18} />
            Export CSV
          </button>
        )}
      </div>

      {toast && (
        <div style={ls.toast} data-testid="lead-toast">
          <Check size={16} /> {toast}
        </div>
      )}

      {leads.length > 0 && (
        <div style={ls.bulkBar} data-testid="bulk-action-bar">
          <button
            onClick={toggleAll}
            style={ls.selectAllBtn}
            data-testid="select-all-leads"
            title={allSelected ? 'Deselecteer alles' : 'Selecteer alles'}
          >
            {allSelected ? <CheckSquare size={16} /> : <Square size={16} />}
            {allSelected ? 'Deselecteer alles' : 'Selecteer alles'}
          </button>

          <div style={ls.statusFilterWrap}>
            <label style={ls.statusFilterLabel}>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={ls.statusFilterSelect}
              data-testid="status-filter-select"
            >
              <option value="all">Alle ({leads.length})</option>
              {Object.entries(statusOptions).map(([slug, label]) => {
                const optLabel = `${label} (${statusCounts[slug] || 0})`;
                return <option key={slug} value={slug}>{optLabel}</option>;
              })}
            </select>
          </div>

          <span style={ls.bulkCount} data-testid="bulk-count">
            {selected.size === 0
              ? 'Geen leads geselecteerd'
              : `${selected.size} lead${selected.size === 1 ? '' : 's'} geselecteerd`}
          </span>
          {selected.size > 0 && (
            <div style={ls.bulkActions}>
              <button
                onClick={bulkCopy}
                style={ls.bulkBtnPrimary}
                data-testid="bulk-copy-button"
              >
                <Copy size={14} /> Kopieer selectie
              </button>
              <button
                onClick={bulkWhatsApp}
                style={ls.bulkBtnWhatsapp}
                data-testid="bulk-whatsapp-button"
              >
                <Send size={14} /> WhatsApp selectie
              </button>
              <button
                onClick={bulkExportCSV}
                style={ls.bulkBtn}
                data-testid="bulk-export-button"
              >
                <Download size={14} /> Export selectie (CSV)
              </button>
              <button
                onClick={bulkDelete}
                style={ls.bulkBtnDanger}
                data-testid="bulk-delete-button"
                title="Verwijder geselecteerde leads definitief"
              >
                <Trash2 size={14} /> Verwijder selectie
              </button>
              <button
                onClick={clearSelection}
                style={ls.bulkBtnGhost}
                data-testid="bulk-clear-button"
              >
                Wis selectie
              </button>
            </div>
          )}
        </div>
      )}

      {leads.length === 0 ? (
        <div style={{ ...s.importStatusCard, textAlign: 'center', padding: '4rem 2rem' }}>
          <Mail size={48} color="#C5A059" style={{ marginBottom: '1rem' }} />
          <p style={s.emptyText}>Nog geen leads ontvangen.</p>
        </div>
      ) : visibleLeads.length === 0 ? (
        <div style={{ ...s.importStatusCard, textAlign: 'center', padding: '3rem 2rem' }}>
          <p style={s.emptyText}>Geen leads met deze status.</p>
        </div>
      ) : (
        <div style={ls.list}>
          {visibleLeads.map((lead) => {
            const idx = leads.indexOf(lead);
            const isOpen = expanded === idx;
            const isCopied = copiedIdx === idx;
            const isSelected = selected.has(idx);
            const statusKey = lead.status || 'new';
            const statusColor = STATUS_COLORS[statusKey] || STATUS_COLORS.new;
            const isSavingStatus = savingStatusId === lead.id;
            return (
              <div key={lead.id || idx} style={isSelected ? { ...ls.card, ...ls.cardSelected } : ls.card} data-testid="lead-row">
                <div style={ls.cardHead}>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleOne(idx); }}
                    style={ls.checkbox}
                    data-testid={`select-lead-${idx}`}
                    title={isSelected ? 'Deselecteer' : 'Selecteer'}
                    aria-label={isSelected ? 'Deselecteer lead' : 'Selecteer lead'}
                  >
                    {isSelected ? <CheckSquare size={18} color="#C5A059" /> : <Square size={18} color="#8C8B88" />}
                  </button>
                  <div
                    style={ls.headInner}
                    onClick={() => setExpanded(isOpen ? null : idx)}
                  >
                  <div style={ls.col}>
                    <div style={ls.name}>{lead.first_name} {lead.last_name || ''}</div>
                    <div style={ls.date}>{formatDate(lead.created_at)}</div>
                  </div>
                  <div style={ls.col}>
                    <a href={`mailto:${lead.email}`} style={ls.email} onClick={(e) => e.stopPropagation()}>
                      {lead.email}
                    </a>
                    {lead.phone && (
                      <a href={`tel:${lead.phone}`} style={ls.phone} onClick={(e) => e.stopPropagation()}>
                        {lead.phone}
                      </a>
                    )}
                  </div>
                  <div style={ls.col}>
                    {lead.property_title ? (
                      <>
                        {lead.property_ref && (
                          <div style={ls.refBadge} data-testid={`lead-ref-${idx}`}>
                            Ref. {lead.property_ref}
                          </div>
                        )}
                        <div style={ls.propertyTitle}>
                          <Home size={14} style={{ verticalAlign: 'middle', marginRight: '0.35rem' }} />
                          {lead.property_title}
                        </div>
                        <div style={ls.propertyMeta}>
                          {lead.property_location}
                          {lead.property_region && lead.property_region !== lead.property_location && ` · ${lead.property_region}`}
                          {lead.property_price && ` · €${lead.property_price.toLocaleString('nl-NL')}`}
                        </div>
                      </>
                    ) : (
                      <div style={ls.propertyMeta}>{lead.source || 'homepage'}</div>
                    )}
                  </div>
                  <div style={ls.statusCol} onClick={(e) => e.stopPropagation()}>
                    <div
                      style={{
                        ...ls.statusBadge,
                        backgroundColor: statusColor.bg,
                        color: statusColor.fg,
                      }}
                      data-testid={`status-badge-${idx}`}
                    >
                      <span style={{ ...ls.statusDot, backgroundColor: statusColor.dot }} />
                      {statusOptions[statusKey] || 'Nieuw'}
                    </div>
                    <select
                      value={statusKey}
                      onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                      style={ls.statusSelect}
                      disabled={isSavingStatus || !lead.id}
                      data-testid={`status-select-${idx}`}
                      aria-label="Wijzig status"
                    >
                      {Object.entries(statusOptions).map(([slug, label]) => (
                        <option key={slug} value={slug}>{label}</option>
                      ))}
                    </select>
                    <div style={ls.ctaSub}>
                      {ctaIcon(lead.cta_type)}
                      <span>{ctaLabel(lead.cta_type)}</span>
                    </div>
                  </div>
                  </div>
                </div>

                {/* Quick action toolbar – always visible */}
                <div style={ls.actions} onClick={(e) => e.stopPropagation()}>
                  <button
                    style={isCopied ? ls.actionBtnSuccess : ls.actionBtn}
                    onClick={() => copyToClipboard(buildLeadText(lead), idx)}
                    data-testid={`copy-lead-${idx}`}
                    title="Kopieer alle leadgegevens"
                  >
                    {isCopied ? <Check size={14} /> : <Copy size={14} />}
                    {isCopied ? 'Gekopieerd' : 'Kopieer lead'}
                  </button>
                  <button
                    style={ls.actionBtn}
                    onClick={() => copyToClipboard(lead.email || '', idx, 'E-mail gekopieerd')}
                    data-testid={`copy-email-${idx}`}
                    title="Alleen e-mail"
                  >
                    <Mail size={14} /> E-mail
                  </button>
                  {lead.phone && (
                    <button
                      style={ls.actionBtn}
                      onClick={() => copyToClipboard(lead.phone, idx, 'Telefoon gekopieerd')}
                      data-testid={`copy-phone-${idx}`}
                      title="Alleen telefoonnummer"
                    >
                      <Phone size={14} /> Telefoon
                    </button>
                  )}
                  <button
                    style={ls.actionBtnWhatsapp}
                    onClick={() => openWhatsApp(lead)}
                    data-testid={`whatsapp-lead-${idx}`}
                    title="Open WhatsApp en kies de groep (of contact) om deze lead te plaatsen"
                  >
                    <Send size={14} /> WhatsApp → groep
                  </button>
                  <button
                    style={ls.actionBtn}
                    onClick={() => openEmail(lead)}
                    data-testid={`email-lead-${idx}`}
                    title={`Doorsturen via e-mail naar ${PARTNER.name} (${PARTNER.email})`}
                  >
                    <ExternalLink size={14} /> Email → Pascal
                  </button>
                  <button
                    style={ls.actionBtn}
                    onClick={() => sendWelcomeMail(lead)}
                    data-testid={`welcome-lead-${idx}`}
                    title="Stuur de welkomstmail nu naar dit e-mailadres"
                  >
                    <MailCheck size={14} /> Welkomstmail
                  </button>
                  <button
                    style={ls.actionBtnDanger}
                    onClick={() => deleteLead(lead)}
                    data-testid={`delete-lead-${idx}`}
                    title="Verwijder deze lead definitief"
                  >
                    <Trash2 size={14} /> Verwijderen
                  </button>
                </div>

                {isOpen && (
                  <div style={ls.cardBody}>
                    {lead.message ? (
                      <div>
                        <strong style={ls.msgLabel}>Bericht:</strong>
                        <p style={ls.msgText}>{lead.message}</p>
                      </div>
                    ) : (
                      <p style={ls.msgText}>Geen bericht meegestuurd.</p>
                    )}
                    {lead.property_url && (
                      <a href={lead.property_url} target="_blank" rel="noopener noreferrer" style={ls.detailLink}>
                        Bekijk woning →
                      </a>
                    )}

                    <NoteEditor
                      key={`note-${lead.id}`}
                      lead={lead}
                      idx={idx}
                      onSave={(text) => saveLeadNote(lead.id, text)}
                    />

                    <StatusTimeline
                      history={lead.status_history || []}
                      statusOptions={statusOptions}
                      createdAt={lead.created_at}
                    />

                    <details style={ls.previewWrap}>
                      <summary style={ls.previewSummary}>Voorbeeld van gekopieerde tekst</summary>
                      <pre style={ls.previewPre}>{buildLeadText(lead)}</pre>
                    </details>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** Notitieveld per lead — onSave wordt aangeroepen bij blur of expliciete klik op
 * Opslaan. Reset gebeurt via de `key` prop van de parent (key={lead.id}) zodat
 * React de component opnieuw mounted bij een ander lead — geen state-in-effect.
 */
const NoteEditor = ({ lead, idx, onSave }) => {
  const [value, setValue] = React.useState(lead.note || '');
  const [dirty, setDirty] = React.useState(false);

  const handleSave = () => {
    if (dirty) {
      onSave(value);
      setDirty(false);
    }
  };

  return (
    <div style={ls.noteWrap}>
      <div style={ls.noteHead}>
        <StickyNote size={14} color="#C5A059" />
        <strong style={ls.noteTitle}>Interne notitie</strong>
        {lead.note_updated_at && (
          <span style={ls.noteMeta}>
            Laatst bijgewerkt: {new Date(lead.note_updated_at).toLocaleString('nl-NL', {
              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
            })}
          </span>
        )}
      </div>
      <textarea
        value={value}
        onChange={(e) => { setValue(e.target.value); setDirty(true); }}
        onBlur={handleSave}
        placeholder="Bv. 'Gebeld 14/06 – wil bezichtiging eind juli. Hypotheek via NL bank.'"
        style={ls.noteTextarea}
        rows={3}
        data-testid={`lead-note-${idx}`}
      />
      <div style={ls.noteActions}>
        <button
          type="button"
          onClick={handleSave}
          disabled={!dirty}
          style={dirty ? ls.noteSaveBtn : ls.noteSaveBtnDisabled}
          data-testid={`lead-note-save-${idx}`}
        >
          {dirty ? 'Opslaan' : 'Opgeslagen'}
        </button>
      </div>
    </div>
  );
};

/** Toont alle status-veranderingen chronologisch + de oorspronkelijke aanmaakdatum. */
const StatusTimeline = ({ history, statusOptions, createdAt }) => {
  const fmt = (iso) => new Date(iso).toLocaleString('nl-NL', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  // Display in reverse-chronological order (newest first)
  const items = [...history].reverse();

  return (
    <div style={ls.timelineWrap}>
      <div style={ls.timelineHead}>
        <Clock size={14} color="#C5A059" />
        <strong style={ls.noteTitle}>Status historie</strong>
      </div>
      <ol style={ls.timelineList}>
        {items.map((entry, i) => (
          <li key={i} style={ls.timelineItem}>
            <span style={{ ...ls.timelineDot, backgroundColor: (STATUS_COLORS[entry.status] || STATUS_COLORS.new).dot }} />
            <span style={ls.timelineStatus}>{statusOptions[entry.status] || entry.status}</span>
            <span style={ls.timelineDate}>{fmt(entry.at)}</span>
            {entry.source === 'admin' && <span style={ls.timelineSource}>handmatig</span>}
          </li>
        ))}
        <li style={ls.timelineItem}>
          <span style={{ ...ls.timelineDot, backgroundColor: '#EAE5DD' }} />
          <span style={ls.timelineStatus}>Lead aangemaakt</span>
          <span style={ls.timelineDate}>{fmt(createdAt)}</span>
        </li>
      </ol>
    </div>
  );
};

const ls = {
  list: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  card: {
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#EAE5DD',
  },
  cardSelected: { borderColor: '#C5A059', boxShadow: '0 0 0 1px #C5A059 inset' },
  cardHead: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '0.75rem',
    padding: '0.5rem 1rem 0.5rem 0.85rem',
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: 'none',
    padding: '0 0.25rem',
    cursor: 'pointer',
    flexShrink: 0,
  },
  headInner: {
    flex: 1,
    minWidth: 0,
    display: 'grid',
    gridTemplateColumns: 'minmax(130px, 1fr) minmax(170px, 1.3fr) minmax(200px, 2fr) minmax(180px, 1.4fr)',
    gap: '1rem',
    padding: '0.5rem 0',
    alignItems: 'center',
    cursor: 'pointer',
  },
  // Bulk action bar
  bulkBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.85rem',
    padding: '0.75rem 1rem',
    backgroundColor: '#FAF9F6',
    border: '1px solid #EAE5DD',
    marginBottom: '0.75rem',
  },
  statusFilterWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
  },
  statusFilterLabel: {
    color: '#585755',
    fontSize: '0.78rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  statusFilterSelect: {
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#EAE5DD',
    color: '#1A1918',
    padding: '0.4rem 0.6rem',
    fontSize: '0.82rem',
    fontFamily: "'Manrope', sans-serif",
    cursor: 'pointer',
    outline: 'none',
  },
  selectAllBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    color: '#1A1918',
    padding: '0.45rem 0.8rem',
    fontSize: '0.8rem',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif",
  },
  bulkCount: { color: '#585755', fontSize: '0.85rem', fontWeight: 500, marginRight: 'auto' },
  bulkActions: { display: 'flex', flexWrap: 'wrap', gap: '0.4rem' },
  bulkBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    color: '#1A1918',
    padding: '0.5rem 0.85rem',
    fontSize: '0.8rem',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif",
  },
  bulkBtnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    backgroundColor: '#C5A059',
    border: '1px solid #C5A059',
    color: 'white',
    padding: '0.5rem 0.85rem',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif",
  },
  bulkBtnWhatsapp: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    backgroundColor: '#25D366',
    border: '1px solid #25D366',
    color: 'white',
    padding: '0.5rem 0.85rem',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif",
  },
  bulkBtnGhost: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    backgroundColor: 'transparent',
    border: '1px solid #EAE5DD',
    color: '#8C8B88',
    padding: '0.5rem 0.85rem',
    fontSize: '0.8rem',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif",
  },
  bulkBtnDanger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    backgroundColor: 'white',
    border: '1px solid #DC2626',
    color: '#DC2626',
    padding: '0.5rem 0.85rem',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif",
  },
  col: { minWidth: 0 },
  name: { color: '#1A1918', fontWeight: 600, fontSize: '0.95rem' },
  date: { color: '#8C8B88', fontSize: '0.78rem', marginTop: '0.15rem' },
  email: { color: '#C5A059', textDecoration: 'none', fontSize: '0.88rem', display: 'block' },
  phone: { color: '#585755', textDecoration: 'none', fontSize: '0.82rem', display: 'block', marginTop: '0.15rem' },
  propertyTitle: {
    color: '#1A1918', fontSize: '0.88rem', fontWeight: 500, marginBottom: '0.15rem',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  propertyMeta: { color: '#8C8B88', fontSize: '0.78rem' },
  refBadge: {
    display: 'inline-block',
    backgroundColor: '#1A1918',
    color: '#C5A059',
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    padding: '0.18rem 0.45rem',
    marginBottom: '0.3rem',
    fontFamily: "'Manrope', monospace",
  },
  statusCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
    minWidth: 0,
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.35rem 0.7rem',
    fontSize: '0.78rem',
    fontWeight: 600,
    width: 'fit-content',
    borderRadius: '999px',
  },
  statusDot: {
    width: '0.5rem',
    height: '0.5rem',
    borderRadius: '999px',
    display: 'inline-block',
  },
  statusSelect: {
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#EAE5DD',
    color: '#1A1918',
    padding: '0.3rem 0.5rem',
    fontSize: '0.78rem',
    fontFamily: "'Manrope', sans-serif",
    cursor: 'pointer',
    outline: 'none',
    width: '100%',
    maxWidth: '220px',
  },
  ctaSub: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3rem',
    color: '#8C8B88',
    fontSize: '0.72rem',
    textTransform: 'capitalize',
  },
  cta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.35rem 0.7rem',
    backgroundColor: '#FAF9F6',
    border: '1px solid #EAE5DD',
    color: '#585755',
    fontSize: '0.75rem',
  },
  ctaText: { fontWeight: 500 },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.4rem',
    padding: '0.65rem 1.25rem',
    borderTop: '1px dashed #EAE5DD',
    backgroundColor: '#FCFBF8',
  },
  actionBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.45rem 0.75rem',
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    color: '#585755',
    fontSize: '0.78rem',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif",
    transition: 'all 0.15s ease',
  },
  actionBtnSuccess: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.45rem 0.75rem',
    backgroundColor: '#C5A059',
    border: '1px solid #C5A059',
    color: 'white',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif",
  },
  actionBtnWhatsapp: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.45rem 0.75rem',
    backgroundColor: '#25D366',
    border: '1px solid #25D366',
    color: 'white',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif",
  },
  actionBtnDanger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.45rem 0.75rem',
    backgroundColor: 'white',
    border: '1px solid #DC2626',
    color: '#DC2626',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif",
    marginLeft: 'auto',
  },
  cardBody: {
    borderTop: '1px solid #EAE5DD',
    padding: '1rem 1.25rem',
    backgroundColor: '#FAF9F6',
  },
  msgLabel: { color: '#1A1918', fontSize: '0.85rem' },
  msgText: { color: '#585755', fontSize: '0.9rem', lineHeight: 1.6, margin: '0.5rem 0 0', whiteSpace: 'pre-wrap' },
  detailLink: {
    display: 'inline-block', marginTop: '0.75rem',
    color: '#C5A059', textDecoration: 'underline', fontSize: '0.85rem',
  },
  previewWrap: { marginTop: '1rem' },
  previewSummary: {
    cursor: 'pointer',
    color: '#8C8B88',
    fontSize: '0.78rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontWeight: 500,
  },
  previewPre: {
    marginTop: '0.5rem',
    padding: '0.85rem',
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    color: '#1A1918',
    fontSize: '0.8rem',
    fontFamily: "'Courier New', monospace",
    whiteSpace: 'pre-wrap',
    lineHeight: 1.6,
    maxHeight: '260px',
    overflow: 'auto',
  },
  // Note editor
  noteWrap: {
    marginTop: '1.25rem',
    padding: '1rem',
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#EAE5DD',
  },
  noteHead: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginBottom: '0.5rem',
  },
  noteTitle: {
    color: '#1A1918',
    fontSize: '0.85rem',
    fontWeight: 600,
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
  },
  noteMeta: { color: '#8C8B88', fontSize: '0.72rem', marginLeft: 'auto' },
  noteTextarea: {
    width: '100%',
    padding: '0.65rem 0.8rem',
    fontFamily: "'Manrope', sans-serif",
    fontSize: '0.88rem',
    color: '#1A1918',
    backgroundColor: '#FAF9F6',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#EAE5DD',
    boxSizing: 'border-box',
    resize: 'vertical',
    outline: 'none',
    lineHeight: 1.5,
  },
  noteActions: { display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' },
  noteSaveBtn: {
    backgroundColor: '#C5A059',
    color: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#C5A059',
    padding: '0.4rem 0.95rem',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif",
  },
  noteSaveBtnDisabled: {
    backgroundColor: '#F5F1E8',
    color: '#8C8B88',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#EAE5DD',
    padding: '0.4rem 0.95rem',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'not-allowed',
    fontFamily: "'Manrope', sans-serif",
  },
  // Status timeline
  timelineWrap: {
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#EAE5DD',
  },
  timelineHead: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.65rem',
  },
  timelineList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
  },
  timelineItem: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto auto',
    alignItems: 'center',
    gap: '0.65rem',
    fontSize: '0.82rem',
    color: '#585755',
    paddingLeft: '0.25rem',
  },
  timelineDot: {
    width: '0.65rem',
    height: '0.65rem',
    borderRadius: '999px',
    display: 'inline-block',
  },
  timelineStatus: { color: '#1A1918', fontWeight: 500 },
  timelineDate: { color: '#8C8B88', fontSize: '0.75rem', whiteSpace: 'nowrap' },
  timelineSource: {
    color: '#C5A059',
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontWeight: 600,
  },
  toast: {
    position: 'fixed',
    top: '1.5rem',
    right: '1.5rem',
    zIndex: 1000,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1.1rem',
    backgroundColor: '#1A1918',
    color: 'white',
    fontSize: '0.85rem',
    fontWeight: 500,
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
  },
};

export default LeadsPanel;
